import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling");
  const [info, setInfo] = useState(null);
  const pollRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (pollRef.current >= 12) {
          setStatus("timeout");
          return;
        }
        pollRef.current += 1;
        setTimeout(poll, 2000);
      } catch {
        if (pollRef.current >= 12) { setStatus("error"); return; }
        pollRef.current += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      <Navbar />
      <div className="max-w-[680px] mx-auto px-6 pt-24 pb-24 text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <>
            <Loader2 className="h-12 w-12 text-brand-primary mx-auto animate-spin" />
            <h1 className="font-display text-4xl uppercase mt-6">Finalizing your subscription…</h1>
            <p className="text-brand-muted mt-2">Hang tight — we're confirming the payment with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto" />
            <h1 className="font-display text-5xl uppercase mt-6">You're in.</h1>
            <p className="text-brand-muted mt-3">
              Welcome to BallOutTV. Boyz Gone Wild and the full network are now unlocked.
            </p>
            <Button data-testid="payment-success-start-watching" onClick={() => nav("/browse")} className="mt-8 bg-brand-primary hover:bg-brand-primaryHover rounded-none px-8 h-12 uppercase tracking-[0.15em]">
              Start watching
            </Button>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <XCircle className="h-14 w-14 text-red-400 mx-auto" />
            <h1 className="font-display text-4xl uppercase mt-6">Payment not completed</h1>
            <p className="text-brand-muted mt-2">{status === "expired" ? "The checkout session expired." : "We couldn't confirm the payment."}</p>
            <Button onClick={() => nav("/subscribe")} className="mt-6 bg-brand-primary hover:bg-brand-primaryHover rounded-none">
              Try again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
